import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Category } from '@/__generated_graphql__/graphql';
import { CategoryModal } from '@/components/CategoryModal/CategoryModal';
import { CategoryModalMobile } from '@/components/CategoryModalMobile/CategoryModalMobile';
import { Portal } from '@/components/Portal/Portal';
import { ClickableChip } from '@/components/ui/ClickableChip/ClickableChip';
import { useNavigate } from '@/hooks/useNavigate';
import { useSearchRoute } from '@/hooks/useSearchRoute';

import * as S from './CategoryPicker.styles';

type CategoryPickerProps = {
  isBig: boolean;
  categories: Category[];
};

export const CategoryPicker: FC<CategoryPickerProps> = ({
  isBig,
  categories
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);

  const { navigate } = useNavigate();

  const SEARCH = useSearchRoute();

  const handleNavigate = (categoryOrCategories: Category | Category[]) => {
    const categoryIds = Array.isArray(categoryOrCategories)
      ? categoryOrCategories.map(item => item?.id)
      : [categoryOrCategories.id];

    navigate({
      route: SEARCH,
      params: {
        categoryIds
      }
    });
  };

  const onClickChip = (jobs: Category[]) => {
    setIsModalOpen(true);
    setSelectedCategories(jobs);
  };

  const allChipLabel = t(`CategoryPicker.${isBig ? 'all' : 'all_short'}`);

  return (
    <>
      <S.Wrapper>
        {categories?.map((category: Category) => (
          <ClickableChip
            id={category.id}
            key={category.id}
            data-testid="clickable-chip"
            label={category.name}
            onClick={() => handleNavigate(category)}
          />
        ))}

        {!!categories?.length && (
          <ClickableChip
            id={'all_categories'}
            key="all_categories"
            data-testid="all-chip"
            label={allChipLabel}
            onClick={() => onClickChip([])}
          />
        )}
      </S.Wrapper>

      <Portal id="category-modal-portal">
        {!isBig ? (
          <CategoryModalMobile
            isOpen={isModalOpen}
            onChange={categoryId => setSelectedCategories(categoryId)}
            value={selectedCategories}
            onClose={() => setIsModalOpen(false)}
            onConfirmCallback={value => {
              handleNavigate(value);
            }}
          />
        ) : (
          <CategoryModal
            isOpen={isModalOpen}
            onChange={categoryId => setSelectedCategories(categoryId)}
            value={selectedCategories}
            onClose={() => setIsModalOpen(false)}
            onConfirmCallback={value => {
              handleNavigate(value);
            }}
          />
        )}
      </Portal>
    </>
  );
};
